import { useEffect, useMemo, useRef, useState } from "react";

interface Options<T> {
  /** Everything wanted, in any order. */
  ids: readonly string[];
  /** Fetched before the rest of `ids`, in this order — the vehicles the reader is looking at. */
  priorityIds?: readonly string[];
  /** How long a fetched entry counts as fresh; it is fetched again after that while still wanted. */
  maxAgeMs: number;
  /** Ids per request. */
  pageSize: number;
  /** Fetches one page; ids it returns nothing for are kept as known-empty until they go stale. */
  fetchPage: (ids: string[], signal: AbortSignal) => Promise<ReadonlyMap<string, T>>;
  /** Changing it drops everything fetched so far, e.g. on a session switch. */
  resetKey: string;
}

interface Entry<T> {
  value: T | undefined;
  fetchedAt: number;
}

interface Worker {
  running: boolean;
  /** The wanted ids changed while a page was running. */
  kicked: boolean;
  timer?: ReturnType<typeof setTimeout>;
  abort: AbortController;
}

const newWorker = (): Worker => ({ running: false, kicked: false, abort: new AbortController() });

/** Wait after a failed page before the next attempt. */
const RETRY_AFTER_MS = 30_000;

/**
 * Fetches data per id, one page at a time, and keeps what it has.
 *
 * A query keyed on the whole id list refetches every id whenever one id joins or leaves — which on
 * the map is every pan — and drops the request in flight when the key moves. With 1800 vehicles
 * that answer never came back. Here an id is only asked for when it has no entry or its entry is
 * older than `maxAgeMs`, priority ids first, and a change of `ids` never cancels a running page.
 */
export function useIncrementalFetch<T>({
  ids,
  priorityIds = [],
  maxAgeMs,
  pageSize,
  fetchPage,
  resetKey,
}: Options<T>): { data: ReadonlyMap<string, T>; updatedAt: number } {
  const storeRef = useRef(new globalThis.Map<string, Entry<T>>());
  const [version, setVersion] = useState(0);

  // The worker reads the latest wishes from refs, so a new list of ids does not restart it.
  const wishesRef = useRef({ ids, priorityIds, maxAgeMs, pageSize, fetchPage });
  wishesRef.current = { ids, priorityIds, maxAgeMs, pageSize, fetchPage };

  const workerRef = useRef<Worker>(newWorker());
  const [wakeUp, setWakeUp] = useState(0);

  useEffect(() => {
    storeRef.current = new globalThis.Map();
    workerRef.current.abort.abort();
    clearTimeout(workerRef.current.timer);
    workerRef.current = newWorker();
    setVersion(current => current + 1);
    return () => {
      workerRef.current.abort.abort();
      clearTimeout(workerRef.current.timer);
    };
  }, [resetKey]);

  const idsKey = useMemo(() => [...priorityIds, "|", ...ids].join(","), [ids, priorityIds]);

  useEffect(() => {
    const worker = workerRef.current;
    if (worker.running) {
      // Picked up when the running page is done, see `finally` below.
      worker.kicked = true;
      return;
    }
    clearTimeout(worker.timer);
    worker.running = true;

    const run = async () => {
      const { signal } = worker.abort;
      try {
        while (!signal.aborted) {
          const wishes = wishesRef.current;
          const now = Date.now();
          const store = storeRef.current;
          const isDue = (id: string) => {
            const entry = store.get(id);
            return !entry || now - entry.fetchedAt >= wishes.maxAgeMs;
          };

          const page: string[] = [];
          const picked = new Set<string>();
          for (const list of [wishes.priorityIds, wishes.ids]) {
            for (const id of list) {
              if (page.length >= wishes.pageSize) break;
              if (!picked.has(id) && isDue(id)) {
                picked.add(id);
                page.push(id);
              }
            }
          }

          if (page.length === 0) {
            // Nothing due: wake up when the oldest wanted entry goes stale.
            if (Number.isFinite(wishes.maxAgeMs)) {
              let oldest = Infinity;
              for (const id of wishes.ids) {
                oldest = Math.min(oldest, store.get(id)?.fetchedAt ?? now);
              }
              const wait = Number.isFinite(oldest) ? Math.max(0, oldest + wishes.maxAgeMs - now) : wishes.maxAgeMs;
              worker.timer = setTimeout(() => setWakeUp(current => current + 1), wait);
            }
            return;
          }

          let result: ReadonlyMap<string, T>;
          try {
            result = await wishes.fetchPage(page, signal);
          } catch (error) {
            if (signal.aborted) return;
            console.error(error);
            worker.timer = setTimeout(() => setWakeUp(current => current + 1), RETRY_AFTER_MS);
            return;
          }
          if (signal.aborted || storeRef.current !== store) return;

          const fetchedAt = Date.now();
          for (const id of page) {
            store.set(id, { value: result.get(id), fetchedAt });
          }
          setVersion(current => current + 1);
        }
      } finally {
        worker.running = false;
        if (worker.kicked && !worker.abort.signal.aborted) {
          worker.kicked = false;
          setWakeUp(current => current + 1);
        }
      }
    };
    void run();
  }, [idsKey, wakeUp, resetKey]);

  return useMemo(() => {
    const data = new globalThis.Map<string, T>();
    let updatedAt = 0;
    for (const [id, entry] of storeRef.current) {
      if (entry.value !== undefined) data.set(id, entry.value);
      updatedAt = Math.max(updatedAt, entry.fetchedAt);
    }
    return { data, updatedAt };
    // `version` stands for the store, which is a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);
}
